import { useEffect, useMemo, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import {
  COLUMN_GAP,
  COLUMN_WIDTH,
  HEIGHT_OF_ONE_HOUR,
  ONE_HOUR,
  TIMELINE_WIDTH,
} from "@miplatform/constants";
import {
  getAdjustedHeight,
  getTimeIntervals,
  isEmpty,
  isToday,
} from "@miplatform/utils";
import { DndContext, DragOverlay } from "@dnd-kit/core";
import { Box, GridLegacy } from "@mui/material";
import { Bucket } from "../Bucket/Bucket";
import { Buckets } from "../Buckets/Buckets";
import { ColumnHeaderContainer } from "../ColumnHeaderContainer/ColumnHeaderContainer";
import { CurrentTimeIndicator } from "../CurrentTimeIndicator/CurrentTimeIndicator";
import { DayColumn } from "../DayColumn/DayColumn";
import { Event } from "../Event/Event";
import { TimeGutter } from "../TimeGutter/TimeGutter";
import type { SchedulerViewProps } from "../types";
import { useDayViewController } from "./useDayViewController";

export function DayView({
  buckets = [],
  ColumnHeader,
  data = [],
  date,
  EventCard,
  groupBy = "assignedUserId",
  increment = ONE_HOUR,
  EmptyUser,
  onColumnHeaderClick,
  onColumnHeaderRefresh,
  onDrag,
  onCardHover,
  onEventCreate,
  onEventUpdate,
  users = [],
  time,
  selectedUserId,
  setSelectedUserId,
  eventClassName,
  attemptsLoading = new Set(),
  hasModalOpen,
  zoom = 1,
  verticalBuckets = null,
}: Readonly<SchedulerViewProps>) {
  const { classes } = useStyles();
  const intervals = useMemo(() => getTimeIntervals(increment), [increment]);

  const {
    sensors,
    columns,
    draggingEvent,
    columnHeaderAreaRef,
    calendarRef,
    selectedEventId,
    setSelectedEventId,
    prepareColumns,
    handleDragStart,
    handleDragCancel,
    handleDragEnd,
    handleScroll,
    snapModifier,
  } = useDayViewController({
    onEventUpdate,
    onEventCreate,
    date,
    intervals,
    zoom,
  });

  const adjustedHeight = useMemo(
    () => getAdjustedHeight(increment, zoom),
    [increment, zoom]
  );

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (groupBy) {
      prepareColumns({ data, groupBy, users });
    }
  }, [data, groupBy, users]);

  useEffect(() => {
    onDrag?.(draggingEvent);
  }, [draggingEvent]);

  useEffect(() => {
    if (containerRef?.current) {
      containerRef.current.scrollTo({ top: HEIGHT_OF_ONE_HOUR * 7 - 40 });
    }
  }, []);

  return (
    <Box className={classes.container}>
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        onDragEnd={handleDragEnd}
        modifiers={[snapModifier]}
        autoScroll
      >
        <Box className={classes.verticalBucketsLayout}>
          {verticalBuckets}
          <Box className={classes.schedulerBody}>
            <Box className={classes.viewContainer} ref={containerRef}>
              <Box className={classes.stickyTopArea}>
                <Box className={classes.columnHeadersTimelineDiv} />
                <Box
                  className={classes.columnsContainer}
                  ref={columnHeaderAreaRef}
                  onScroll={handleScroll(calendarRef)}
                >
                  {columns.map(([key]) => {
                    const user = users?.find((user) => user.id === key);
                    return (
                      <ColumnHeaderContainer key={key}>
                        {ColumnHeader && (
                          <ColumnHeader
                            user={user}
                            onClick={onColumnHeaderClick}
                            date={date}
                            isSelected={selectedUserId === user?.id}
                            setSelectedUserId={setSelectedUserId}
                            onRefresh={onColumnHeaderRefresh}
                          />
                        )}
                      </ColumnHeaderContainer>
                    );
                  })}
                </Box>
              </Box>
              <Box className={classes.calendarContainer} data-testid="day-view">
                <TimeGutter
                  intervals={intervals}
                  adjustedHeight={adjustedHeight}
                />
                <GridLegacy
                  className={classes.columnsContainer}
                  ref={calendarRef}
                  onScroll={handleScroll(columnHeaderAreaRef)}
                  container
                >
                  {columns.map(([key, events, deactivatedIntervals]) => (
                    <DayColumn
                      id={key}
                      adjustedHeight={adjustedHeight}
                      key={key}
                      intervals={intervals}
                      deactivatedIntervals={deactivatedIntervals}
                    >
                      {events.map((event) => (
                        <Event
                          key={event.id}
                          hasModalOpen={hasModalOpen}
                          event={event}
                          isOverLay={false}
                          className={
                            typeof eventClassName === "function"
                              ? eventClassName(event)
                              : eventClassName
                          }
                          onSelect={() => setSelectedEventId(event.id)}
                          onDeselect={handleDragCancel}
                          onEventUpdate={onEventUpdate}
                          zoom={zoom}
                        >
                          {EventCard && (
                            <EventCard
                              event={event}
                              isSelected={selectedEventId === event.id}
                              onCardHover={onCardHover}
                              loading={attemptsLoading.has(event.id)}
                            />
                          )}
                        </Event>
                      ))}
                    </DayColumn>
                  ))}
                </GridLegacy>
                <CurrentTimeIndicator
                  width={
                    (COLUMN_WIDTH + COLUMN_GAP) * columns.length - COLUMN_GAP
                  }
                  time={time}
                  visible={isToday(date)}
                  zoom={zoom}
                />
              </Box>
            </Box>
            {isEmpty(users) && EmptyUser && <EmptyUser />}
            <GridLegacy
              container
              item
              direction="column"
              className={classes.bucketContainer}
            >
              <Buckets>
                {buckets.map((bucket) => (
                  <Bucket key={bucket.id || bucket.name} bucket={bucket}>
                    {bucket?.data?.map((event) => (
                      <Event
                        key={event.id}
                        hasModalOpen={hasModalOpen}
                        event={event}
                        isInBucket
                        isOverLay={false}
                        onEventUpdate={onEventUpdate}
                        className={
                          typeof eventClassName === "function"
                            ? eventClassName(event)
                            : eventClassName
                        }
                        onSelect={() => setSelectedEventId(event.id)}
                        onDeselect={handleDragCancel}
                      >
                        {EventCard && (
                          <EventCard
                            event={event}
                            isSelected={selectedEventId === event.id}
                            isInBucket
                            onCardHover={onCardHover}
                          />
                        )}
                      </Event>
                    ))}
                  </Bucket>
                ))}
              </Buckets>
            </GridLegacy>
          </Box>
        </Box>
        {draggingEvent && (
          <DragOverlay modifiers={[snapModifier]}>
            <Event
              className={
                typeof eventClassName === "function"
                  ? eventClassName(draggingEvent)
                  : eventClassName
              }
              event={draggingEvent}
              hasModalOpen={hasModalOpen}
              onEventUpdate={onEventUpdate}
              onDeselect={handleDragCancel}
              zoom={zoom}
              isOverLay
            >
              {EventCard && (
                <EventCard
                  event={draggingEvent}
                  isSelected={selectedEventId === draggingEvent.id}
                />
              )}
            </Event>
          </DragOverlay>
        )}
      </DndContext>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    overflow: "hidden",
  },
  verticalBucketsLayout: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  schedulerBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    overflow: "hidden",
  },
  viewContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    overflow: "auto",
    backgroundColor: theme.palette.background.default,
  },
  calendarContainer: {
    display: "flex",
    width: "fit-content",
    position: "relative",
    flex: 1,
  },
  columnsContainer: {
    flexWrap: "nowrap",
    display: "flex",
    gap: COLUMN_GAP,
    flex: 1,
  },
  columnHeadersTimelineDiv: {
    width: TIMELINE_WIDTH,
    backgroundColor: theme.palette.common.white,
    position: "sticky",
    left: 0,
    zIndex: theme.zIndex.drawer,
  },
  bucketContainer: {
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    zIndex: 1,
  },
  stickyTopArea: {
    backgroundColor: theme.palette.background.default,
    display: "flex",
    flexDirection: "row",
    width: "fit-content",
    position: "sticky",
    top: 0,
    zIndex: theme.zIndex.drawer,
  },
}));
